import { inject, PLATFORM_ID } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Router } from '@angular/router'
import { AuthService } from '../../services/auth.service'
import {
  login,
  loginSuccess,
  loginFailure,
  logout,
  registerFailure,
  registerSuccess,
  register,
  forgotPassword,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordFailure,
  refreshToken,
  refreshTokenSuccess,
  refreshTokenFailure,
  loadCurrentUser,
  loadCurrentUserSuccess,
  loadCurrentUserFailure,
  loadSubscription,
  loadSubscriptionSuccess,
  loadSubscriptionFailure,
  logoutFailure,
  logoutSuccess,
  loadPermissions,
  loadPermissionsSuccess,
  loadPermissionsFailure,
  restoreSession,
  revertSwitchUserSuccess,
  revertSwitchUserFailure,
  revertSwitchUser,
  switchUserFailure,
  switchUserSuccess,
  switchUser
} from './auth.actions'
import {
  map,
  mergeMap,
  catchError,
  tap,
  filter,
  take,
  switchMap,
  withLatestFrom,
  exhaustMap
} from 'rxjs/operators'
import { EMPTY, fromEvent, Observable, of } from 'rxjs'
import { eps } from '../../services/url.service'
import { ToastrService } from 'ngx-toastr'
import { selectUser } from './auth.selectors'
import { Store } from '@ngrx/store'
import { HttpClient } from '@angular/common/http'
import { DeviceService } from '../../services/device.service'
import { SubscriptionsService, SubscriptionStatus } from '../../services/subscriptions'
import dayjs from 'dayjs'
import { selectSubscription } from './auth.state'
import { UserService } from '../../services/user.service'
import { isPlatformBrowser } from '@angular/common'

// function getRedirectPath(user: any, subscription: any): string {
//   if (user?.employmentType === 'Admin') {
//     return eps.user.dashboardPath
//   }

//   if (subscription && subscription.expiryDate) {
//     const isExpired = dayjs().isAfter(dayjs(subscription.expiryDate))
//     if (isExpired) {
//       if (user?.employmentType === 'Business Owner') {
//         return eps.planAndBilling.planPath
//       }
//       // Business Owner ke alawa koi bhi role ho, expired subscription pe login pe bhej do
//       return eps.auth.loginPath
//     }
//   }

//   // Default: non-admin, valid/no-subscription-check-needed user
//   return eps.user.employeeDashboardPath
// }

export const loadToken$ = createEffect(
  (platformId = inject(PLATFORM_ID)) => {
    const isBrowser = isPlatformBrowser(platformId)

    return of(isBrowser ? localStorage.getItem('accessToken') : null).pipe(
      map(accessToken => {
        if (!accessToken) {
          return refreshTokenFailure()
        }
        const userJson = localStorage.getItem('user')
        const user = userJson ? JSON.parse(userJson) : null
        const subscriptionJson = localStorage.getItem('subscription')
        const subscription = subscriptionJson ? JSON.parse(subscriptionJson) : null
        const permissionsRaw = localStorage.getItem('permissions')

        const permissions: Record<string, string[]> = {}
        if (permissionsRaw) {
          try {
            const parsed = JSON.parse(permissionsRaw)
            if (Array.isArray(parsed)) {
              parsed.forEach((p: any) => {
                if (p?.moduleName) {
                  permissions[p.moduleName] = p.permissions || []
                }
              })
            }
          } catch (err) {
            console.error('Permission parse failed', err)
          }
        }

        return restoreSession({
          // ✅ ab ye naya action hai, loginSuccess nahi
          accessToken,
          user,
          subscription,
          isAuthenticated: true,
          error: null,
          loading: false,
          showThankYouMessage: false,
          refreshToken: localStorage.getItem('accessToken'),
          permissions,
          authLoaded: true
        })
      })
    )
  },
  { functional: true }
)

//export const loadToken$ = createEffect(
//  () => {
//    return of(localStorage.getItem('accessToken')).pipe(
//      filter((accessToken): accessToken is string => !!accessToken),
//      mergeMap(accessToken =>
//        validateToken(accessToken).pipe( // validateToken is auth service method that sends the token to the backend for validation and returns the authenticated user if valid
//          map(user => loginSuccess({ accessToken, user })),
//          catchError(() => of(logout()))
//        )
//      )
//    )
//  },
//  { functional: true }
//)

export const auth$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    http = inject(HttpClient),
    deviceService = inject(DeviceService)
  ) =>
    actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) =>
        authService.login(email, password).pipe(
          // map(({ accessToken, user, refreshToken, subscription }) => {
          map((response: any) => {
            const { accessToken, user, refreshToken, subscription, deviceId, deviceToken } =
              response
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('accessToken', accessToken as string)
            localStorage.setItem('refreshToken', refreshToken as string)
            localStorage.setItem('subscription', JSON.stringify(subscription))
            if (deviceId) {
              localStorage.setItem('deviceId', deviceId)
            }

            if (deviceToken) {
              localStorage.setItem('deviceToken', deviceToken)
            }
            const isOwner = user?.userRoles?.some((ur: any) => ur.role?.name === 'Business Owner')
            const status = subscription?.subscriptionStatus
            if (status === SubscriptionStatus.PastDue && isOwner) {
              toastr.error(
                'Payment Failed',
                'Your last payment failed. We are retrying the charge, but please update your billing details immediately to avoid service interruption.'
              )
            } else if (
              isOwner &&
              (status === SubscriptionStatus.Expired || status === SubscriptionStatus.Cancelled)
            ) {
              // BLOCKING: Warn user that access is revoked
              toastr.error(
                'Subscription Expired',
                'Your subscription has expired. Access to services is restricted until you renew.'
              )
            } else if (status === SubscriptionStatus.Trialing && isOwner) {
              // EXISTING TRIAL LOGIC
              if (subscription?.expiryDate) {
                const expiry = dayjs(subscription.expiryDate)
                const now = dayjs()

                // Calculate difference in days.
                // e.g., 0.1 days left becomes 1 day. -1 days becomes 0.
                const remainingDays = Math.max(0, Math.ceil(expiry.diff(now, 'day', true)))

                let message = ''
                if (remainingDays > 1) {
                  message = `You have ${remainingDays} days remaining in your trial.`
                  toastr.info('Trial Active', message)
                } else if (remainingDays === 1) {
                  message = `This is the last day of your trial subscription.`
                  toastr.warning('Trial Ending', message)
                } else {
                  message = `Activate your subscription! Your trial subscription has expired.`
                  toastr.error('Trial Expired', message)
                }
              } else {
                toastr.warning(
                  'Trial Subscription',
                  `You have a trial subscription for ${subscription?.trialDays} days.`
                )
              }
            }

            return loginSuccess({
              accessToken,
              user,
              subscription,
              isAuthenticated: true,
              error: null,
              loading: false,
              showThankYouMessage: false,
              refreshToken,
              permissions: {},
              authLoaded: true
            })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'There was an error during signIn.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(loginFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

// export const redirectAfterLogin$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
//     actions$.pipe(
//       ofType(loginSuccess),
//       withLatestFrom(store.select(selectUser), store.select(selectSubscription)),
//       filter(([_, user, __]) => !!user && !!user.employmentType),
//       tap(([_, user, subscription]) => {
//         if (user?.employmentType === 'Admin') {
//           router.navigate([eps.user.dashboardPath])
//         } else if (subscription && subscription.expiryDate) {
//           const isExpired = dayjs().isAfter(dayjs(subscription.expiryDate))
//           if (isExpired) {
//             if (user?.employmentType === 'Business Owner') {
//               // router.navigate([eps.planAndBilling.upgradePlanPath])
//               router.navigate([eps.planAndBilling.planPath])
//               return
//             }
//             // For any other role that is NOT admin, log them out.
//             if (user?.employmentType !== 'Admin') {
//               store.dispatch(logout())
//               localStorage.removeItem('permissions')
//               router.navigate([eps.auth.loginPath])
//               return
//             }
//             // Note: If the role is 'Admin', they will fall through and be handled below.
//           }
//         } else {
//           router.navigate([eps.user.employeeDashboardPath])
//         }
//       })
//     ),
//   { functional: true, dispatch: false }
// )

// export const removeToken$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), toastr = inject(ToastrService)) =>
//     actions$.pipe(
//       ofType(logout),
//       tap(() => {
//         toastr.clear()
//         localStorage.removeItem('accessToken')
//         localStorage.removeItem('user')
//         router.navigate([eps.auth.loginPath]) // 👈 redirect to login
//       })
//     ),
//   { functional: true, dispatch: false }
// )

// export const removeToken$ = createEffect(
//   (
//     actions$ = inject(Actions),
//     authService = inject(AuthService),
//     toastr = inject(ToastrService),
//     router = inject(Router)
//   ) =>
//     actions$.pipe(
//       ofType(logout),
//       mergeMap(() =>
//         authService.logout().pipe(
//           map(res => {
//             toastr.clear()
//             localStorage.removeItem('accessToken')
//             localStorage.removeItem('user')

//             router.navigate([eps.auth.loginPath])

//             return logoutSuccess({ success: res.success || 'Logged out successfully' })
//           }),
//           catchError(error => {
//             const errorMessage = error?.error?.message || 'Session expired. Please log in again.'

//             toastr.error(errorMessage, 'Session Error')
//             toastr.clear()
//             localStorage.removeItem('accessToken')
//             localStorage.removeItem('user')
//             router.navigate([eps.auth.loginPath])
//             // still force logout on error
//             // localStorage.removeItem('accessToken')
//             // localStorage.removeItem('user')
//             // router.navigate([eps.auth.loginPath])

//             return of(logoutFailure({ error: errorMessage }))
//           })
//         )
//       )
//     ),
//   { functional: true }
// )

// export const redirectAfterLogin$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
//     actions$.pipe(
//       ofType(loadPermissionsSuccess),

//       withLatestFrom(store.select(selectUser), store.select(selectSubscription)),

//       filter(([_, user]) => !!user && !!user.employmentType),

//       tap(([_, user, subscription]) => {
//         if (user?.employmentType === 'Admin') {
//           router.navigate([eps.user.dashboardPath])
//           return
//         } else if (subscription?.expiryDate) {
//           const isExpired = dayjs().isAfter(dayjs(subscription.expiryDate))

//           if (isExpired) {
//             if (user?.employmentType === 'Business Owner') {
//               router.navigate([eps.planAndBilling.planPath])
//               return
//             }

//             router.navigate([eps.auth.loginPath])
//             return
//           }
//         } else {
//           router.navigate([eps.user.employeeDashboardPath])
//         }
//       })
//     ),
//   {
//     functional: true,
//     dispatch: false
//   }
// )

function isAdminUser(user: any): boolean {
  return (
    user?.employmentType === 'Admin' ||
    !!user?.userRoles?.some((ur: any) => ur.role?.name === 'Admin')
  )
}

function isSubscriptionExpired(subscription: any): boolean {
  return !!subscription?.expiryDate && dayjs().isAfter(dayjs(subscription.expiryDate))
}

// function isSubscriptionExpired(subscription: any): boolean {
//   return (
//     !!subscription?.expiryDate &&
//     dayjs().isAfter(
//       dayjs(subscription.expiryDate).add(subscription.plan.gracePeriodHours ?? 0, 'hour')
//     )
//   )
// }

// function getRedirectPath(user: any, subscription: any, toastr: ToastrService): string {
//   if (isAdminUser(user)) return eps.user.dashboardPath

//   if (isSubscriptionExpired(subscription)) {
//     toastr.warning(
//       'Your subscription has expired, but you can still access services during the grace period. Please renew your subscription as soon as possible to avoid service interruption.',
//       'Subscription Expired'
//     )
//     if (user?.employmentType === 'Business Owner') return eps.planAndBilling.planPath
//     return eps.auth.loginPath
//   }
//   return eps.user.employeeDashboardPath
// }

function isInGracePeriod(subscription: any): boolean {
  if (!subscription?.expiryDate) return false

  const expiryDate = dayjs(subscription.expiryDate)
  const gracePeriodEnd = expiryDate.add(subscription.plan?.gracePeriodHours ?? 0, 'hour')

  return dayjs().isAfter(expiryDate) && dayjs().isBefore(gracePeriodEnd)
}

function getRedirectPath(user: any, subscription: any, toastr: ToastrService): string {
  if (isAdminUser(user)) return eps.user.dashboardPath

  if (isInGracePeriod(subscription)) {
    toastr.warning(
      'Your subscription has expired, but you can still access services during the grace period. Please renew your subscription as soon as possible to avoid service interruption.',
      'Subscription Expired'
    )
  }

  if (
    // dayjs().isAfter(
    //   dayjs(subscription?.expiryDate).add(subscription?.plan?.gracePeriodHours ?? 0, 'hour')
    // )
    isSubscriptionExpired(subscription)
  ) {
    if (user?.employmentType === 'Business Owner') {
      return eps.planAndBilling.planPath
    }

    return eps.auth.loginPath
  }

  return eps.user.employeeDashboardPath
}

export const redirectAfterLogin$ = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    store = inject(Store),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(loginSuccess),
      switchMap(({ user, subscription }) => {
        if (!user) return EMPTY

        const target = getRedirectPath(user, subscription, toastr)
        // const needsPermissions = !isAdminUser(user) && !isSubscriptionExpired(subscription)
        const needsPermissions = !isAdminUser(user) && target !== eps.auth.loginPath

        const ready$: Observable<unknown> = needsPermissions
          ? actions$.pipe(ofType(loadPermissionsSuccess, loadPermissionsFailure), take(1))
          : of(null)

        return ready$.pipe(map((): { target: string } => ({ target })))
      }),
      tap(({ target }) => {
        if (target === eps.auth.loginPath) {
          toastr.error(
            'Subscription expired. Please contact your business owner.',
            'Access Restricted'
          )
          store.dispatch(logout())
          return
        }
        router.navigate([target])
      })
    ),
  { functional: true, dispatch: false }
)

export const removeToken$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(logout),
      exhaustMap(() => {
        toastr.clear()
        // localStorage.removeItem('accessToken')
        // localStorage.removeItem('user')
        // router.navigate([eps.auth.loginPath])

        return authService.logout().pipe(
          // map(() => logoutSuccess({ success: 'Logged out' })),
          map(() => {
            localStorage.setItem('logout-event', Date.now().toString())
            // ✅ Clear everything on success
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('user')
            localStorage.removeItem('permissions')
            localStorage.removeItem('subscription')
            localStorage.removeItem('fcmToken')
            localStorage.clear()

            router.navigate([eps.auth.loginPath])

            return logoutSuccess({ success: 'Logged out' })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'Session expired. Please log in again.'

            toastr.error(errorMessage, 'Session Error')
            toastr.clear()
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('user')
            localStorage.removeItem('permissions')
            localStorage.removeItem('subscription')
            localStorage.removeItem('fcmToken')

            localStorage.clear()

            router.navigate([eps.auth.loginPath])
            // still force logout on error
            // localStorage.removeItem('accessToken')
            // localStorage.removeItem('user')
            // router.navigate([eps.auth.loginPath])

            return of(logoutFailure({ error: errorMessage }))
          })
        )
      })
    ),
  { functional: true }
)

export const register$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService), // Inject the ToastrService
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(register),
      exhaustMap(
        ({
          firstName,
          lastName,
          email,
          mobile,
          businessName,
          planId,
          mode,
          country,
          state,
          gstNumber
        }) =>
          authService
            .register({
              firstName,
              lastName,
              email,
              mobile,
              businessName,
              planId,
              country,
              state,
              gstNumber
            })
            .pipe(
              // map(() => {
              //   toastr.success('User Registered successfully!', 'Success') // Show success message
              //   return registerSuccess({ success: '' }) // Dispatch success action
              // }),
              map(response => {
                toastr.success('User Registered successfully!', 'Success')
                if (mode === 'buy') {
                  router.navigate(['/auth/chooseSubscription', response.id])
                } else {
                  // TRIAL → no redirect
                  // just stay on registration page / show thank you message
                }
                return registerSuccess({
                  success: '',
                  userId: response.id
                })
              }),
              catchError(error => {
                const errorMessage =
                  error?.error?.message || 'There was an error during registration.'
                toastr.error(errorMessage, 'Error') // Show error message
                return of(registerFailure({ error: errorMessage }))
              })
            )
      )
    ),
  { functional: true }
)

export const forgotPassword$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService) // Inject the ToastrService
  ) =>
    actions$.pipe(
      ofType(forgotPassword), // Action triggered for forgot password
      exhaustMap(({ email }) =>
        authService.forgotPassword({ email }).pipe(
          map(() => {
            toastr.success('Password reset link sent successfully!', 'Success') // Show success message
            return forgotPasswordSuccess({
              message: 'Password reset link sent successfully!'
            }) // Dispatch success action
          }),
          catchError(error => {
            const errorMessage =
              error?.message || 'There was an error during password reset request.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(forgotPasswordFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const resetPassword$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService), // Inject the ToastrService
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(resetPassword), // Action triggered for reset password
      exhaustMap(({ token, email, password }) =>
        authService.resetPassword({ token, email, password }).pipe(
          map(() => {
            toastr.success('Password reset successfully!', 'Success') // Show success message
            router.navigate([eps.auth.loginPath])
            return resetPasswordSuccess({
              message: 'Password reset successfully!'
            }) // Dispatch success action
          }),
          catchError(error => {
            const errorMessage = error?.message || 'There was an error during password reset.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(resetPasswordFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const refreshToken$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(refreshToken),
      switchMap(() =>
        authService.refreshToken().pipe(
          map(({ accessToken, user }) => {
            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('user', JSON.stringify(user))
            return refreshTokenSuccess({ accessToken, user }) // ✅ user is declared now
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'Session expired. Please log in again.'
            toastr.error(errorMessage, 'Session Error')
            return of(refreshTokenFailure())
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadCurrentUser$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService), router = inject(Router)) =>
    actions$.pipe(
      ofType(loadCurrentUser),
      switchMap(() =>
        authService.getCurrentUser().pipe(
          // map(user => loadCurrentUserSuccess({ user })),
          tap(({ accessToken, user, refreshToken }) => {
            // Side effect: save tokens to localStorage
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('accessToken', accessToken ?? '')
            localStorage.setItem('refreshToken', refreshToken ?? '')
          }),
          map(({ accessToken, user, refreshToken }) => {
            if (!user) throw new Error('No user returned')
            const accessTokens = localStorage.getItem('accessToken')
            const refreshTokens = localStorage.getItem('refreshToken')

            if (
              !accessTokens ||
              accessTokens === 'null' ||
              !refreshTokens ||
              refreshTokens === 'null'
            ) {
              router.navigate([eps.auth.loginPath])
            }
            return loadCurrentUserSuccess({ user, accessToken, refreshToken })
          }),
          catchError(err =>
            of(loadCurrentUserFailure({ error: err?.error?.message || 'Failed to fetch user' }))
          )
        )
      )
    ),
  { functional: true }
)

export const loadSubscription$ = createEffect(
  (
    actions$ = inject(Actions),
    subscriptionsService = inject(SubscriptionsService),
    toastr = inject(ToastrService) // Inject the ToastrService
  ) =>
    actions$.pipe(
      ofType(loadSubscription),
      switchMap(action =>
        subscriptionsService.getSubscriptions(action.businessId).pipe(
          map(subscription => loadSubscriptionSuccess({ subscription: subscription[0] })),
          catchError(error => of(loadSubscriptionFailure({ error })))
        )
      )
    ),
  { functional: true }
)

//7may2026
export const loadPermissions$ = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(loadPermissions),
      switchMap(() =>
        userService.getUserPermissions().pipe(
          map(permissions => {
            localStorage.setItem('permissions', JSON.stringify(permissions))

            return loadPermissionsSuccess({ permissions })
          }),
          catchError(error =>
            of(loadPermissionsFailure({ error: error?.message || 'Failed permissions load' }))
          )
        )
      )
    ),
  { functional: true }
)

// export const afterLoginLoadPermissions$ = createEffect(
//   (actions$ = inject(Actions)) =>
//     actions$.pipe(
//       ofType(loginSuccess),
//       map(() => loadPermissions()) // 🔥 trigger API
//     ),
//   { functional: true }
// )
// export const afterLoginLoadPermissions$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router)) =>
//     actions$.pipe(
//       ofType(loginSuccess, restoreSession),
//       switchMap(() => [loadPermissions()])
//     ),
//   { functional: true }
// )

// export const afterLoginLoadPermissions$ = createEffect(
//   (actions$ = inject(Actions)) =>
//     actions$.pipe(
//       ofType(loginSuccess, restoreSession, switchUserSuccess, revertSwitchUserSuccess),
//       switchMap(() => [loadPermissions()])
//     ),
//   { functional: true }
// )

export const afterLoginLoadPermissions$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(loginSuccess, restoreSession, switchUserSuccess, revertSwitchUserSuccess),
      map(() => loadPermissions())
    ),
  { functional: true }
)

// export const redirectAfterRegister$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router)) =>
//     actions$.pipe(
//       ofType(registerSuccess),
//       tap(({ userId }) => {
//         router.navigate(['/auth/chooseSubscription', userId])
//       })
//     ),
//   {
//     functional: true,
//     dispatch: false
//   }
// )

// export const redirectAfterRegister$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
//     actions$.pipe(
//       ofType(registerSuccess),
//       withLatestFrom(store.select(selectRegistrationMode)), // or pass mode via action
//       tap(([action, mode]) => {
//         if (mode === 'buy') {
//           router.navigate(['/auth/chooseSubscription', action.userId])
//         } else {
//           // trial → no redirect
//           // just stay on page
//         }
//       })
//     ),
//   { functional: true, dispatch: false }
// )

export const switchUser$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(switchUser),
      exhaustMap(({ userId }) =>
        authService.switchUser(userId).pipe(
          map((response: any) => {
            const { accessToken, refreshToken, user, subscription } = response

            if (localStorage.getItem('isImpersonating') !== 'true') {
              localStorage.setItem('originalAccessToken', localStorage.getItem('accessToken') || '')
              localStorage.setItem(
                'originalRefreshToken',
                localStorage.getItem('refreshToken') || ''
              )
              localStorage.setItem('originalUser', localStorage.getItem('user') || '')
              localStorage.setItem(
                'originalSubscription',
                localStorage.getItem('subscription') || ''
              )
              localStorage.setItem('originalPermissions', localStorage.getItem('permissions') || '')
            }

            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('refreshToken', refreshToken)
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('subscription', JSON.stringify(subscription))
            localStorage.setItem('isImpersonating', 'true')

            // router.navigate([eps.user.dashboardPath])
            // window.location.href = eps.user.employeeDashboardPath
            const redirectPath = getRedirectPath(user, subscription, toastr)
            window.location.href = redirectPath

            // permissions yahan set nahi kar rahe — loadPermissions$ effect karega
            return switchUserSuccess({ accessToken, refreshToken, user, subscription })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'User switch failed.'
            toastr.error(errorMessage, 'Error')
            return of(switchUserFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const revertSwitchUser$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(revertSwitchUser),
      exhaustMap(() =>
        authService.revertSwitchUser().pipe(
          map((response: any) => {
            const { accessToken, user, subscription } = response

            // localStorage.setItem('accessToken', accessToken)
            // if (user) localStorage.setItem('user', JSON.stringify(user))
            // if (subscription) localStorage.setItem('subscription', JSON.stringify(subscription))

            // 👇 Original values ko localStorage se le kar current keys me wapas daalo
            const originalAccessToken = localStorage.getItem('originalAccessToken') || ''
            const originalRefreshToken = localStorage.getItem('originalRefreshToken') || ''
            const originalUser = localStorage.getItem('originalUser')
            const originalSubscription = localStorage.getItem('originalSubscription')
            const originalPermissions = localStorage.getItem('originalPermissions')

            localStorage.setItem('accessToken', originalAccessToken)
            localStorage.setItem('refreshToken', originalRefreshToken)
            if (originalUser) localStorage.setItem('user', originalUser)
            if (originalSubscription) localStorage.setItem('subscription', originalSubscription)
            if (originalPermissions) localStorage.setItem('permissions', originalPermissions)

            localStorage.removeItem('originalAccessToken')
            localStorage.removeItem('originalRefreshToken')
            localStorage.removeItem('originalUser')
            localStorage.removeItem('originalSubscription')
            localStorage.removeItem('originalPermissions')
            localStorage.removeItem('isImpersonating')

            // router.navigate([eps.user.employeeDashboardPath])
            window.location.href = eps.user.dashboardPath

            return revertSwitchUserSuccess({ accessToken, user, subscription })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'Failed to switch back.'
            toastr.error(errorMessage, 'Error')
            return of(revertSwitchUserFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const afterSwitchUserLoadPermissions$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(switchUserSuccess, revertSwitchUserSuccess),
      switchMap(() => [loadPermissions()])
    ),
  { functional: true }
)

// export const syncLogoutAcrossTabs$ = createEffect(
//   (actions$ = inject(Actions), platformId = inject(PLATFORM_ID)) => {
//     const isBrowser = isPlatformBrowser(platformId)

//     if (!isBrowser) {
//       return of() // SSR ya non-browser context me kuch mat karo
//     }

//     return fromEvent<StorageEvent>(window, 'storage').pipe(
//       filter(event => event.key === 'logout-event' && event.newValue !== null),
//       map(() => logoutSuccess({ success: 'Logged out from another tab' }))
//     )
//   },
//   { functional: true }
// )

export const syncLogoutAcrossTabs$ = createEffect(
  (actions$ = inject(Actions), platformId = inject(PLATFORM_ID)) => {
    if (!isPlatformBrowser(platformId)) {
      return EMPTY
    }

    return fromEvent<StorageEvent>(window, 'storage').pipe(
      filter(
        event =>
          event.key === 'logout-event' || // normal logout flow
          (event.key === null && event.newValue === null) // localStorage.clear() flow
      ),
      map(() => logoutSuccess({ success: 'Logged out from another tab' }))
    )
  },
  { functional: true }
)

export const authEffects = {
  loadToken$,
  auth$,
  redirectAfterLogin$,
  removeToken$,
  register$,
  forgotPassword$,
  resetPassword$,
  refreshToken$,
  loadCurrentUser$,
  loadSubscription$,
  loadPermissions$,
  afterLoginLoadPermissions$,
  // redirectAfterRegister$,
  switchUser$,
  revertSwitchUser$,
  afterSwitchUserLoadPermissions$,
  syncLogoutAcrossTabs$
}
